import { toast } from 'App/components/ToastContainer'

import { toastDefaultOptions } from '../globalConstants'
import i18n from '../i18n'

export const DEFAULT_RECORD_TIME: number = 1000 * 60 * 2 + 500 // 2 min 0.5sec
const DEFAULT_RECORD_TYPE = 'audio/mpeg'
const DEFAULT_CONSTRAINTS: MediaStreamConstraints = { video: false, audio: true }

type TStopProps = {
  immediate?: boolean
  audioType?: string
}

export type TStopRecordReturn = {
  audioUrl: string
  audioBlob: Blob
}

export type TRecordAudioReturn = {
  start: () => Promise<TStopRecordReturn>
  stop: (props?: TStopProps) => Promise<TStopRecordReturn>
  cancel: () => void
}

export const recordAudio = async (): Promise<TRecordAudioReturn | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(DEFAULT_CONSTRAINTS)

    const mediaRecorder = new MediaRecorder(stream)
    const audioChunks: BlobPart[] = []

    mediaRecorder.addEventListener('dataavailable', ({ data }) => {
      audioChunks.push(data)
    })

    const releaseMicrophone = () => stream.getTracks().forEach((track) => track.stop())

    let audioType = DEFAULT_RECORD_TYPE
    let recorded: Promise<TStopRecordReturn> | undefined

    const result = () => {
      if (!recorded) {
        recorded = new Promise<TStopRecordReturn>((resolve) => {
          const settle = () => {
            const audioBlob = new Blob(audioChunks, { type: audioType })

            resolve({ audioBlob, audioUrl: URL.createObjectURL(audioBlob) })
          }

          if (mediaRecorder.state === 'inactive') {
            settle()

            return
          }

          mediaRecorder.addEventListener('stop', settle, { once: true })
        })
      }

      return recorded
    }

    const start = () => {
      mediaRecorder.start()

      return result()
    }

    const stop = ({ immediate = false, audioType: type }: TStopProps = {}) => {
      if (type) {
        audioType = type
      }

      const pending = result()

      if (immediate && mediaRecorder.state === 'recording') {
        releaseMicrophone()
        mediaRecorder.stop()
      }

      return pending
    }

    const cancel = () => {
      releaseMicrophone()

      if (mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop()
      }

      recorded?.then(({ audioUrl }) => URL.revokeObjectURL(audioUrl))
    }

    return { start, stop, cancel }
  } catch (e) {
    toast.error(i18n.t('chat.blockedMicroError'), toastDefaultOptions)

    return null
  }
}
